import { PatientConversationStaffOption } from "@/modules/patient-conversations/composables/staff-directory"

/** The grammar the server parses pages with: `@[usr_…]` (TDP §4.2.3). Providers never see the raw token. */
export const MENTION_TOKEN = /@\[(usr_[A-Za-z0-9_-]+)\]/g

// Longest staff name worth matching after an "@"; past this the user is clearly not searching for a person
const MAX_MENTION_QUERY_CHARS = 40
const MENTION_TRIGGER = new RegExp(`(?:^|\\s)@([^@\\n]{0,${MAX_MENTION_QUERY_CHARS}})$`)

/** An "@" before the caret that is still being typed: where it starts and the text after it. */
export interface MentionTrigger {
  start: number
  query: string
}

/** The "@Full Name" a page reads as while the note is being written; namesakes carry their role in the name. */
export function mentionText(person: Pick<PatientConversationStaffOption, "displayName">): string {
  return `@${person.displayName}`
}

/** An "@" at the start of a word before the caret, with the text typed after it; `undefined` when there is none. */
export function findMentionTrigger(text: string, caret: number): MentionTrigger | undefined {
  const beforeCaret = text.slice(0, caret)
  const match = MENTION_TRIGGER.exec(beforeCaret)
  if (!match) return undefined
  const query = match[1]
  return { start: beforeCaret.length - query.length - 1, query }
}

// A page is the whole name: "@Ann Lee" must not claim the start of "@Ann Lees" or "@Ann Lee-Smith",
// but "@Ann Lee's patient" still pages her
const NOT_MID_WORD = "(?![\\p{L}\\p{N}-])"

function mentionPattern(person: Pick<PatientConversationStaffOption, "displayName">, tail = ""): RegExp {
  const escaped = mentionText(person).replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
  return new RegExp(`${escaped}${NOT_MID_WORD}${tail}`, "gu")
}

/** Whether this person's "@Full Name" appears in the text as a whole word. */
export function hasMention(text: string, person: Pick<PatientConversationStaffOption, "displayName">): boolean {
  return mentionPattern(person).test(text)
}

/** Swaps each paged name for its token. Longest names first so "@Ann Lee-Smith" is never half-replaced by "@Ann Lee". */
export function toWireContent(text: string, pagedStaff: ReadonlyArray<PatientConversationStaffOption>): string {
  return [...pagedStaff]
    .sort((a, b) => b.displayName.length - a.displayName.length)
    .reduce((wire, person) => wire.replace(mentionPattern(person), `@[${person.id}]`), text)
}

/** The text without this person's page, taking the space the picker inserted after the name with it. */
export function withoutMention(text: string, person: PatientConversationStaffOption): string {
  return text.replace(mentionPattern(person, " ?"), "")
}
